/**
 * AiChatCostEstimator — live widget for the Cloud GM AI Control Hub homepage.
 *
 * Renders a compact card that shows:
 *   • Total conversations + messages in the last N days
 *   • Total tokens (in + out)
 *   • Estimated cost in USD
 *   • Per-platform breakdown (cloudgm_customer / cloudgm_business / asb606_admin)
 *   • Per-model breakdown (which model burned the most $)
 *
 * Backed by  GET /api/admin/ai-cost/summary?days=N.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { DollarSign, Loader2, RefreshCw, TrendingUp, Zap, Bell, AlertTriangle, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_META = {
  cloudgm_customer: { ar: 'CloudGM كستمر', en: 'CloudGM Customer', color: 'text-cyan-300', bg: 'bg-cyan-500/10 border-cyan-500/30' },
  cloudgm_business: { ar: 'CloudGM بزنس',  en: 'CloudGM Business', color: 'text-emerald-300', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  asb606_admin:     { ar: 'ASB606 أدمن',   en: 'ASB606 Admin',     color: 'text-amber-300', bg: 'bg-amber-500/10 border-amber-500/30' },
};

const RANGE_OPTIONS = [
  { days: 1,  labelAr: 'اليوم',       labelEn: 'Today' },
  { days: 7,  labelAr: 'آخر ٧ أيام',  labelEn: 'Last 7d' },
  { days: 30, labelAr: 'آخر ٣٠ يوم',  labelEn: 'Last 30d' },
  { days: 90, labelAr: 'آخر ٩٠ يوم',  labelEn: 'Last 90d' },
];

const fmtCost = (n) => `$${(Number(n) || 0).toFixed(4)}`;
const fmtK = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
};

export default function AiChatCostEstimator({ language }) {
  const isAr = language === 'ar';
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [thresholdStatus, setThresholdStatus] = useState(null);
  const [thresholds, setThresholds] = useState([]);
  const [showThresholds, setShowThresholds] = useState(false);
  const [editing, setEditing] = useState({});   // { [service_type]: { limit_usd, enabled } }
  const [saving, setSaving] = useState(null);

  const load = async (rangeDays = days) => {
    setLoading(true);
    setError(null);
    try {
      const [summ, tList, tStatus] = await Promise.all([
        axios.get(`${API_URL}/api/admin/ai-cost/summary?days=${rangeDays}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/ai-cost/thresholds`, { headers: getAuthHeaders() }).catch(() => ({ data: { thresholds: [] } })),
        axios.get(`${API_URL}/api/admin/ai-cost/thresholds/status`, { headers: getAuthHeaders() }).catch(() => ({ data: { alerts: [], usage: [] } })),
      ]);
      setData(summ.data);
      setThresholds(tList.data?.thresholds || []);
      setThresholdStatus(tStatus.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveThreshold = async (service_type, limit_usd, enabled = true) => {
    setSaving(service_type);
    try {
      await axios.put(
        `${API_URL}/api/admin/ai-cost/thresholds`,
        { service_type, monthly_limit_usd: Number(limit_usd) || 0, enabled },
        { headers: getAuthHeaders() },
      );
      toast.success(isAr ? `✅ حُفظ حدّ ${service_type}` : `✅ Saved threshold for ${service_type}`);
      setEditing((s) => ({ ...s, [service_type]: undefined }));
      load(days);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setSaving(null);
    }
  };

  const deleteThreshold = async (service_type) => {
    setSaving(service_type);
    try {
      await axios.delete(`${API_URL}/api/admin/ai-cost/thresholds/${service_type}`, { headers: getAuthHeaders() });
      toast.success(isAr ? `🗑 حُذف حدّ ${service_type}` : `🗑 Removed threshold for ${service_type}`);
      load(days);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Delete failed'));
    } finally {
      setSaving(null);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const totals = data?.totals || { in: 0, out: 0, cost: 0, convos: 0, messages: 0 };
  const platforms = Object.entries(data?.by_platform || {}).sort((a, b) => b[1].cost - a[1].cost);
  const models = Object.entries(data?.by_model || {}).sort((a, b) => b[1].cost - a[1].cost).slice(0, 5);

  return (
    <div
      className="rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.04] to-amber-500/[0.02] p-5"
      data-testid="ai-cost-estimator"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
            <DollarSign className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-sm">
              {isAr ? 'مقدّر تكلفة الذكاء الاصطناعي' : 'AI Cost Estimator'}
            </h3>
            <p className="text-[11px] text-white/50 mt-0.5">
              {isAr
                ? 'تحليل مباشر لاستهلاك الرموز والتكلفة عبر كل المنصّات'
                : 'Live token & cost breakdown across all platforms'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* Range selector */}
          <div className="flex gap-1 bg-black/30 p-0.5 rounded-lg">
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                className={`px-2 py-1 text-[10px] rounded-md transition ${
                  days === r.days ? 'bg-emerald-500/20 text-emerald-300' : 'text-white/50 hover:text-white'
                }`}
                data-testid={`cost-range-${r.days}`}
              >
                {isAr ? r.labelAr : r.labelEn}
              </button>
            ))}
          </div>
          <button
            onClick={() => load(days)}
            disabled={loading}
            className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition"
            data-testid="cost-refresh"
            aria-label="refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-400/50" />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-amber-300 text-xs" data-testid="cost-error">
          ⚠️ {error}
        </div>
      ) : (
        <>
          {/* Threshold alerts banner (red for exceeded, amber for warning) */}
          {thresholdStatus?.alerts?.length > 0 && (
            <div className="mb-4 space-y-1.5" data-testid="cost-threshold-alerts">
              {thresholdStatus.alerts.map((a) => (
                <div
                  key={a.service_type}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs border ${
                    a.severity === 'high'
                      ? 'bg-red-500/15 border-red-500/40 text-red-200'
                      : 'bg-amber-500/15 border-amber-500/40 text-amber-200'
                  }`}
                >
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1">{isAr ? a.message_ar : a.message_en}</span>
                  <span className="font-mono text-[10px] opacity-70">{a.percent_used}%</span>
                </div>
              ))}
            </div>
          )}

          {/* Big totals row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
            <div className="rounded-xl bg-black/40 border border-white/5 p-3">
              <div className="text-[10px] text-white/40 mb-1">
                {isAr ? 'التكلفة الإجماليّة' : 'Total cost'}
              </div>
              <div className="text-lg font-bold text-emerald-300 font-mono" data-testid="cost-total-usd">
                {fmtCost(totals.cost)}
              </div>
            </div>
            <div className="rounded-xl bg-black/40 border border-white/5 p-3">
              <div className="text-[10px] text-white/40 mb-1">
                {isAr ? 'محادثات' : 'Conversations'}
              </div>
              <div className="text-lg font-bold text-white font-mono">{fmtK(totals.convos)}</div>
            </div>
            <div className="rounded-xl bg-black/40 border border-white/5 p-3">
              <div className="text-[10px] text-white/40 mb-1">
                {isAr ? 'رسائل' : 'Messages'}
              </div>
              <div className="text-lg font-bold text-white font-mono">{fmtK(totals.messages)}</div>
            </div>
            <div className="rounded-xl bg-black/40 border border-white/5 p-3">
              <div className="text-[10px] text-white/40 mb-1">
                {isAr ? 'الرموز (in / out)' : 'Tokens (in / out)'}
              </div>
              <div className="text-[13px] font-bold text-white/80 font-mono leading-tight">
                {fmtK(totals.in)} <span className="text-white/40">/</span> {fmtK(totals.out)}
              </div>
            </div>
          </div>

          {/* Provider cards — Gemini vs Claude */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[
              { key: 'google', label: 'Gemini', color: 'text-blue-300', bg: 'bg-blue-500/[0.06] border-blue-500/30', bar: 'bg-blue-400' },
              { key: 'anthropic', label: 'Claude', color: 'text-orange-300', bg: 'bg-orange-500/[0.06] border-orange-500/30', bar: 'bg-orange-400' },
            ].map((p) => {
              const d = data?.by_provider?.[p.key] || { cost: 0, calls: 0, tokens: 0 };
              return (
                <div key={p.key} className={`rounded-xl border ${p.bg} p-3`} data-testid={`cost-provider-${p.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-black ${p.color}`}>{p.label}</span>
                    <Zap className={`h-3.5 w-3.5 ${p.color}`} />
                  </div>
                  <div className={`text-lg font-bold font-mono ${p.color}`} dir="ltr">{fmtCost(d.cost)}</div>
                  <div className="text-[10px] text-white/40 font-mono mt-0.5" dir="ltr">
                    {fmtK(d.calls)} {isAr ? 'نداء' : 'calls'} · {fmtK(d.tokens)} tok
                  </div>
                </div>
              );
            })}
          </div>

          {/* Platform-group cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
            {[
              { key: 'customer', ar: 'منصة العملاء', en: 'Customer Platform', color: 'text-cyan-300', bg: 'bg-cyan-500/[0.06] border-cyan-500/30' },
              { key: 'business', ar: 'منصة التجار', en: 'Merchant Platform', color: 'text-emerald-300', bg: 'bg-emerald-500/[0.06] border-emerald-500/30' },
              { key: 'admin', ar: 'لوحة التحكم', en: 'Admin Panel', color: 'text-amber-300', bg: 'bg-amber-500/[0.06] border-amber-500/30' },
              { key: 'fitness', ar: '606 فتنس', en: '606 Fitness', color: 'text-green-300', bg: 'bg-green-500/[0.06] border-green-500/30', soon: true },
            ].map((p) => {
              const d = data?.platform_groups?.[p.key] || { cost: 0, calls: 0 };
              return (
                <div key={p.key} className={`rounded-xl border ${p.bg} p-3 relative`} data-testid={`cost-group-${p.key}`}>
                  {p.soon && (
                    <span className="absolute top-2 end-2 px-1.5 py-0.5 rounded bg-white/10 text-[8px] font-bold text-white/50">
                      {isAr ? 'قريباً' : 'SOON'}
                    </span>
                  )}
                  <div className={`text-[11px] font-bold ${p.color} mb-1`}>{isAr ? p.ar : p.en}</div>
                  <div className={`text-base font-bold font-mono ${p.color}`} dir="ltr">{fmtCost(d.cost)}</div>
                  <div className="text-[10px] text-white/40 font-mono mt-0.5" dir="ltr">
                    {fmtK(d.calls)} {isAr ? 'نداء' : 'calls'}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Per-platform breakdown */}
          {platforms.length > 0 && (
            <div className="mb-4">
              <h4 className="text-[11px] font-bold text-white/60 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3" />
                {isAr ? 'التفصيل حسب المنصّة' : 'By platform'}
              </h4>
              <div className="space-y-1.5">
                {platforms.map(([platform, p]) => {
                  const meta = PLATFORM_META[platform] || { ar: platform, en: platform, color: 'text-white', bg: 'bg-white/5 border-white/10' };
                  const pct = totals.cost > 0 ? (p.cost / totals.cost) * 100 : 0;
                  return (
                    <div
                      key={platform}
                      className={`rounded-lg border ${meta.bg} p-2.5 flex items-center gap-3`}
                      data-testid={`cost-platform-${platform}`}
                    >
                      <div className={`text-xs font-bold ${meta.color} flex-shrink-0 min-w-[110px]`}>
                        {isAr ? meta.ar : meta.en}
                      </div>
                      <div className="flex-1 flex items-center gap-2 min-w-0">
                        <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <div
                            className={`h-full ${meta.color.replace('text-', 'bg-').replace('-300', '-400/70')}`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-white/40 font-mono w-12 text-end">
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="text-xs font-mono text-emerald-300 flex-shrink-0 min-w-[70px] text-end">
                        {fmtCost(p.cost)}
                      </div>
                      <div className="text-[10px] text-white/40 flex-shrink-0 hidden sm:block">
                        {p.convos} {isAr ? 'محادثة' : 'convo'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top-5 models */}
          {models.length > 0 && (
            <div>
              <h4 className="text-[11px] font-bold text-white/60 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Zap className="h-3 w-3" />
                {isAr ? 'أعلى ٥ نماذج استهلاكاً' : 'Top-5 consuming models'}
              </h4>
              <div className="rounded-lg border border-white/5 bg-black/20 divide-y divide-white/5">
                {models.map(([model, m]) => (
                  <div key={model} className="px-3 py-1.5 flex items-center justify-between gap-2 text-[11px]">
                    <span className="text-white/80 font-mono truncate" title={model}>{model}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-white/40">{fmtK(m.in + m.out)} tok</span>
                      <span className="text-emerald-300 font-mono min-w-[62px] text-end">{fmtCost(m.cost)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {platforms.length === 0 && !loading && (
            <div className="text-center py-8 text-white/40 text-xs" data-testid="cost-empty">
              {isAr
                ? 'لا توجد محادثات مسجّلة في هذه الفترة.'
                : 'No conversations recorded in this range.'}
            </div>
          )}

          {/* Monthly threshold management */}
          <div className="mt-5 pt-4 border-t border-white/5">
            <button
              onClick={() => setShowThresholds(!showThresholds)}
              className="w-full flex items-center justify-between text-[11px] text-white/60 hover:text-white transition"
              data-testid="cost-toggle-thresholds"
            >
              <span className="flex items-center gap-1.5">
                <Bell className="h-3 w-3" />
                {isAr ? 'عتبات الإنذار الشهريّة' : 'Monthly alert thresholds'}
                {thresholds.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-white/10 text-[9px]">{thresholds.length}</span>
                )}
              </span>
              <span className="text-white/40 text-[10px]">
                {showThresholds ? (isAr ? 'إخفاء' : 'hide') : (isAr ? 'إظهار' : 'show')}
              </span>
            </button>
            {showThresholds && (
              <div className="mt-3 space-y-2" data-testid="cost-thresholds-panel">
                {/* Existing thresholds — status + edit */}
                {(thresholdStatus?.usage || []).map((u) => {
                  const meta = PLATFORM_META[u.service_type] || { ar: u.service_type, en: u.service_type };
                  const isEditing = editing[u.service_type] !== undefined;
                  return (
                    <div
                      key={u.service_type}
                      className="rounded-lg border border-white/10 bg-black/20 p-2.5"
                      data-testid={`cost-threshold-row-${u.service_type}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-white">{isAr ? meta.ar : meta.en}</div>
                          <div className="text-[10px] text-white/40 font-mono">
                            ${u.used_usd.toFixed(2)} / ${u.limit_usd} · {u.percent_used}%
                          </div>
                        </div>
                        {isEditing ? (
                          <>
                            <input
                              type="number"
                              step="0.01"
                              value={editing[u.service_type]}
                              onChange={(e) => setEditing((s) => ({ ...s, [u.service_type]: e.target.value }))}
                              className="w-20 px-2 py-1 bg-black/40 border border-white/10 rounded text-xs text-white font-mono"
                              data-testid={`cost-threshold-input-${u.service_type}`}
                            />
                            <button
                              onClick={() => saveThreshold(u.service_type, editing[u.service_type])}
                              disabled={saving === u.service_type}
                              className="p-1 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-40"
                              data-testid={`cost-threshold-save-${u.service_type}`}
                            >
                              <Check className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => setEditing((s) => ({ ...s, [u.service_type]: undefined }))}
                              className="p-1 rounded bg-white/5 text-white/60 hover:bg-white/10"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="flex-1 max-w-[100px] h-1 rounded-full bg-white/5 overflow-hidden">
                              <div
                                className={`h-full transition-all ${
                                  u.state === 'exceeded' ? 'bg-red-400' :
                                  u.state === 'warning' ? 'bg-amber-400' : 'bg-emerald-400'
                                }`}
                                style={{ width: `${Math.min(100, u.percent_used)}%` }}
                              />
                            </div>
                            <button
                              onClick={() => setEditing((s) => ({ ...s, [u.service_type]: String(u.limit_usd) }))}
                              className="text-[10px] px-2 py-0.5 rounded text-white/60 hover:text-white bg-white/5 hover:bg-white/10"
                            >
                              {isAr ? 'تعديل' : 'edit'}
                            </button>
                            <button
                              onClick={() => deleteThreshold(u.service_type)}
                              disabled={saving === u.service_type}
                              className="text-[10px] px-2 py-0.5 rounded text-red-300/70 hover:text-red-300 bg-red-500/5 hover:bg-red-500/15 disabled:opacity-40"
                            >
                              {isAr ? 'حذف' : 'remove'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Add threshold for platforms not yet configured */}
                {(() => {
                  const existing = new Set(thresholds.map((t) => t.service_type));
                  const available = Object.keys(PLATFORM_META).filter((k) => !existing.has(k));
                  if (!available.length) return null;
                  return (
                    <div className="rounded-lg border border-dashed border-white/10 p-2.5" data-testid="cost-threshold-add">
                      <div className="text-[10px] text-white/40 mb-1.5">
                        {isAr ? 'إضافة عتبة جديدة:' : 'Add new threshold:'}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {available.map((k) => {
                          const meta = PLATFORM_META[k];
                          return (
                            <button
                              key={k}
                              onClick={() => saveThreshold(k, 50)}
                              disabled={saving === k}
                              className={`text-[10px] px-2.5 py-1 rounded-md border ${meta.bg} ${meta.color} hover:opacity-80 disabled:opacity-40`}
                              data-testid={`cost-threshold-add-${k}`}
                            >
                              + {isAr ? meta.ar : meta.en} ($50)
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </>
      )}

      {data?.as_of && (
        <p className="mt-3 text-[9px] text-white/30 text-end font-mono" dir="ltr">
          updated {new Date(data.as_of).toLocaleTimeString()}
        </p>
      )}
    </div>
  );
}
